import { money } from "@/lib/api";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Loader2, PackageOpen } from "lucide-react";

export function Logo({ className, dark, iconOnly }) {
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <img src="/viperowl-icon.png" alt="VIPER OWL" className="h-9 w-9 shrink-0 object-contain" />
      {!iconOnly && (
        <span className={cn("font-heading text-xl font-bold tracking-tight", dark ? "text-white" : "text-forest")}>
          VIPER<span className="text-lime drop-shadow-[0_1px_1px_rgba(8,71,52,0.5)]"> OWL</span>
        </span>
      )}
    </div>
  );
}

export function Money({ value, className, strike }) {
  return (
    <span className={cn("num", strike && "line-through text-msage", className)}>{money(value)}</span>
  );
}

const STATUS_STYLES = {
  VERIFIED: "bg-psage text-forest", PAID: "bg-psage text-forest", DELIVERED: "bg-forest text-white",
  DISPATCHED: "bg-olive text-white", IN_TRANSIT: "bg-olive text-white", CONFIRMED: "bg-psage text-forest",
  PROCESSING: "bg-psage text-forest", SETTLED: "bg-forest text-white", ACTIVE: "bg-psage text-forest",
  PENDING: "bg-amber-100 text-amber-800", VERIFICATION_PENDING: "bg-amber-100 text-amber-800",
  AWAITING_PAYMENT: "bg-amber-100 text-amber-800", MOBILE_VERIFIED: "bg-psage text-forest",
  REJECTED: "bg-red-100 text-red-700", SUSPENDED: "bg-red-100 text-red-700", FAILED: "bg-red-100 text-red-700",
  OUT_OF_STOCK: "bg-red-100 text-red-700", REQUESTED: "bg-amber-100 text-amber-800",
};

export function StatusBadge({ status, className }) {
  const s = (status || "").toUpperCase();
  return (
    <span data-testid={`status-${s.toLowerCase()}`}
      className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        STATUS_STYLES[s] || "bg-sgrey text-ink", className)}>
      {s.replace(/_/g, " ")}
    </span>
  );
}

export function MetricCard({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="overline text-olive">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-msage" />}
      </div>
      <div className={cn("num mt-2 text-3xl font-semibold", accent ? "text-forest" : "text-ink")}>{value}</div>
      {sub && <div className="mt-1 text-sm text-ink/60">{sub}</div>}
    </div>
  );
}

export function EmptyState({ title, hint, icon: Icon = PackageOpen, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-msage bg-psage/20 px-6 py-14 text-center">
      <Icon className="h-10 w-10 text-msage" />
      <p className="mt-4 font-heading text-lg font-medium text-forest">{title}</p>
      {hint && <p className="mt-1 max-w-sm text-sm text-ink/60">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Loader({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-forest">
      <Loader2 className="h-5 w-5 animate-spin" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function Stagger({ children, className }) {
  return (
    <motion.div className={className}
      initial="hidden" animate="show"
      variants={{ show: { transition: { staggerChildren: 0.05 } } }}>
      {children}
    </motion.div>
  );
}
export const staggerItem = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};
